import { FillPattern } from '../types/floorplannerTypes';

export type FillPatternCategory = 'material' | 'paving' | 'organic';

export interface FillPatternDefinition {
  id: FillPattern;
  label: string;
  category: FillPatternCategory;
  shaderType: number;
  worldScale: number;
  strokeWidth: number;
}

const WORLD_SCALE_MULTIPLIER = 0.72;
const STROKE_WIDTH_MULTIPLIER = 0.72;
const HERRINGBONE_PLANK_LENGTH = 0.6;
const HERRINGBONE2_PLANK_LENGTH = 0.4;
const CHEVRON_PLANK_LENGTH = 0.6;
const STONE_LARGE_CELL_SIZE = 0.5;
const STONE_SMALL_CELL_SIZE = 0.3;
const HERRINGBONE_SEGMENT_TO_SCALE_RATIO = Math.hypot(1 / 3.8, 0.48 / 1.82);
const HERRINGBONE2_PLANK_TO_SCALE_RATIO = 80 / 256;
const CHEVRON_PLANK_TO_SCALE_RATIO = 1 / 4.4;
const STONE_LARGE_GRID_COUNT = 6.2;
const STONE_SMALL_GRID_COUNT = 10.5;
const HERRINGBONE_WORLD_SCALE = HERRINGBONE_PLANK_LENGTH / HERRINGBONE_SEGMENT_TO_SCALE_RATIO;
const HERRINGBONE2_WORLD_SCALE = HERRINGBONE2_PLANK_LENGTH / HERRINGBONE2_PLANK_TO_SCALE_RATIO;
const CHEVRON_WORLD_SCALE = CHEVRON_PLANK_LENGTH / CHEVRON_PLANK_TO_SCALE_RATIO;
const STONE_LARGE_WORLD_SCALE = STONE_LARGE_CELL_SIZE * STONE_LARGE_GRID_COUNT;
const STONE_SMALL_WORLD_SCALE = STONE_SMALL_CELL_SIZE * STONE_SMALL_GRID_COUNT;
const GRASS_WORLD_SCALE = 1.2;
const CEMENT_WORLD_SCALE = 2.0;
const CONCRETE_WORLD_SCALE = 2.0;
const CONCRETE_BLOCK_WORLD_SCALE = 1.75;
const STEEL_WORLD_SCALE = 1.4;

function sourceWorldScale(finalWorldScale: number): number {
  return finalWorldScale / WORLD_SCALE_MULTIPLIER;
}

function definePattern(
  definition: FillPatternDefinition,
  adjustments: {
    worldScaleMultiplier?: number;
    strokeWidthMultiplier?: number;
  } = {},
): FillPatternDefinition {
  return {
    ...definition,
    worldScale:
      definition.worldScale *
      WORLD_SCALE_MULTIPLIER *
      (adjustments.worldScaleMultiplier ?? 1),
    strokeWidth:
      definition.strokeWidth *
      STROKE_WIDTH_MULTIPLIER *
      (adjustments.strokeWidthMultiplier ?? 1),
  };
}

export function canonicalizeFillPattern(pattern: FillPattern | undefined): FillPattern | undefined {
  switch (pattern) {
    case 'flooring':
    case 'parquet':
      return 'herringbone';
    case 'tiles':
      return 'paversOctagon';
    case 'hatch45':
      return 'rcc';
    case 'crosshatch':
      return 'concreteBlock';
    default:
      return pattern;
  }
}

const patternOrder: FillPattern[] = [
  'wood',
  'woodContour',
  'grass',
  'brick',
  'sand',
  'cement',
  'concrete',
  'stoneLarge',
  'stoneSmall',
  'earth',
  'concreteBlock',
  'steel',
  'glass',
  'rcc',
  'paversOctagon',
  'pavementGrid',
  'basketweave',
  'runningPavers',
  'staggeredPavers',
  'herringbone',
  'herringbone2',
  'chevron',
  'chevronLines',
];

const patternDefinitions: Record<FillPattern, FillPatternDefinition | undefined> = {
  wood: definePattern(
    { id: 'wood', label: 'Wood Grain', category: 'organic', shaderType: 1, worldScale: 0.9, strokeWidth: 0.028 },
    { worldScaleMultiplier: 0.8, strokeWidthMultiplier: 0.56 },
  ),
  woodContour: definePattern(
    { id: 'woodContour', label: 'Wood Contour', category: 'organic', shaderType: 2, worldScale: 0.95, strokeWidth: 0.028 },
    { worldScaleMultiplier: 0.82, strokeWidthMultiplier: 0.52 },
  ),
  grass: definePattern({ id: 'grass', label: 'Grass', category: 'organic', shaderType: 3, worldScale: sourceWorldScale(GRASS_WORLD_SCALE), strokeWidth: 0.025 }),
  brick: definePattern(
    { id: 'brick', label: 'Brick', category: 'material', shaderType: 4, worldScale: 0.9, strokeWidth: 0.024 },
    { worldScaleMultiplier: 0.78, strokeWidthMultiplier: 0.56 },
  ),
  sand: definePattern(
    { id: 'sand', label: 'Sand / Plaster', category: 'material', shaderType: 5, worldScale: 0.42, strokeWidth: 0.026 },
    { worldScaleMultiplier: 0.92, strokeWidthMultiplier: 1.08 },
  ),
  cement: definePattern(
    { id: 'cement', label: 'Cement', category: 'material', shaderType: 6, worldScale: sourceWorldScale(CEMENT_WORLD_SCALE), strokeWidth: 0.021 },
    { strokeWidthMultiplier: 1.7 },
  ),
  concrete: definePattern({ id: 'concrete', label: 'Concrete', category: 'material', shaderType: 7, worldScale: sourceWorldScale(CONCRETE_WORLD_SCALE), strokeWidth: 0.022 }),
  stoneLarge: definePattern(
    { id: 'stoneLarge', label: 'Stone Large', category: 'organic', shaderType: 8, worldScale: sourceWorldScale(STONE_LARGE_WORLD_SCALE), strokeWidth: 0.024 },
    { strokeWidthMultiplier: 0.72 },
  ),
  stoneSmall: definePattern(
    { id: 'stoneSmall', label: 'Stone Small', category: 'organic', shaderType: 9, worldScale: sourceWorldScale(STONE_SMALL_WORLD_SCALE), strokeWidth: 0.02 },
    { strokeWidthMultiplier: 0.8 },
  ),
  earth: definePattern(
    { id: 'earth', label: 'Earth', category: 'material', shaderType: 10, worldScale: 0.76, strokeWidth: 0.02 },
    { worldScaleMultiplier: 1.15, strokeWidthMultiplier: 0.82 },
  ),
  concreteBlock: definePattern(
    { id: 'concreteBlock', label: 'Concrete Block', category: 'material', shaderType: 11, worldScale: sourceWorldScale(CONCRETE_BLOCK_WORLD_SCALE), strokeWidth: 0.022 },
    { strokeWidthMultiplier: 0.34 },
  ),
  steel: definePattern(
    { id: 'steel', label: 'Steel', category: 'material', shaderType: 12, worldScale: sourceWorldScale(STEEL_WORLD_SCALE), strokeWidth: 0.022 },
    { strokeWidthMultiplier: 0.34 },
  ),
  glass: definePattern(
    { id: 'glass', label: 'Glass', category: 'material', shaderType: 13, worldScale: 1.25, strokeWidth: 0.022 },
    { worldScaleMultiplier: 0.9, strokeWidthMultiplier: 0.21 },
  ),
  rcc: definePattern(
    { id: 'rcc', label: 'R.C.C.', category: 'material', shaderType: 14, worldScale: 0.72, strokeWidth: 0.022 },
    { worldScaleMultiplier: 0.92, strokeWidthMultiplier: 0.34 },
  ),
  paversOctagon: definePattern(
    { id: 'paversOctagon', label: 'Pavement Octagon', category: 'paving', shaderType: 15, worldScale: 0.84, strokeWidth: 0.024 },
    { worldScaleMultiplier: 0.58, strokeWidthMultiplier: 0.31 },
  ),
  pavementGrid: definePattern(
    { id: 'pavementGrid', label: 'Pavement Grid', category: 'paving', shaderType: 16, worldScale: 0.86, strokeWidth: 0.024 },
    { worldScaleMultiplier: 0.95, strokeWidthMultiplier: 0.31 },
  ),
  basketweave: definePattern(
    { id: 'basketweave', label: 'Basketweave', category: 'paving', shaderType: 17, worldScale: 1.13, strokeWidth: 0.024 },
    { worldScaleMultiplier: 0.95, strokeWidthMultiplier: 0.31 },
  ),
  runningPavers: definePattern(
    { id: 'runningPavers', label: 'Running Pavers', category: 'paving', shaderType: 18, worldScale: 0.44, strokeWidth: 0.024 },
    { worldScaleMultiplier: 0.95, strokeWidthMultiplier: 0.31 },
  ),
  staggeredPavers: definePattern(
    { id: 'staggeredPavers', label: 'Staggered Pavers', category: 'paving', shaderType: 19, worldScale: 0.82, strokeWidth: 0.024 },
    { worldScaleMultiplier: 1.04, strokeWidthMultiplier: 0.86 },
  ),
  herringbone: definePattern(
    { id: 'herringbone', label: 'Chevron', category: 'paving', shaderType: 20, worldScale: sourceWorldScale(HERRINGBONE_WORLD_SCALE), strokeWidth: 0.024 },
    { strokeWidthMultiplier: 0.31 },
  ),
  herringbone2: definePattern(
    { id: 'herringbone2', label: 'Herringbone', category: 'paving', shaderType: 23, worldScale: sourceWorldScale(HERRINGBONE2_WORLD_SCALE), strokeWidth: 0.024 },
    { strokeWidthMultiplier: 0.22 },
  ),
  chevron: definePattern(
    { id: 'chevron', label: 'Parquet version', category: 'paving', shaderType: 21, worldScale: sourceWorldScale(CHEVRON_WORLD_SCALE), strokeWidth: 0.024 },
    { strokeWidthMultiplier: 0.31 },
  ),
  chevronLines: definePattern(
    { id: 'chevronLines', label: 'Chevron Lines', category: 'paving', shaderType: 22, worldScale: 0.92, strokeWidth: 0.022 },
    { worldScaleMultiplier: 0.74, strokeWidthMultiplier: 0.34 },
  ),
  flooring: undefined,
  parquet: undefined,
  tiles: undefined,
  hatch45: undefined,
  crosshatch: undefined,
};

export const FILL_PATTERN_OPTIONS: { value: FillPattern | undefined; label: string }[] = [
  { value: undefined, label: 'None' },
  ...patternOrder.map((pattern) => ({
    value: pattern,
    label: patternDefinitions[pattern]!.label,
  })),
];

export function getFillPatternDefinition(
  pattern: FillPattern | undefined,
): FillPatternDefinition | undefined {
  const resolved = canonicalizeFillPattern(pattern);
  if (!resolved) {
    return undefined;
  }
  return patternDefinitions[resolved];
}
